/**
 * 利润场景数据生成和自动计算逻辑
 */
import { ScenarioData } from './types';

/**
 * 生成默认的利润推演场景数据
 */
export const generateDefaultScenarios = (): ScenarioData[] => [
    {
        key: '1',
        name: '设定广告占比计算回报',
        dailyUnits: 20,
        days: 30,
        tacosTarget: 15.0,
        cvr: 5.0,
        cpc: 1.5,
        strategy: '固定竞价',
        placement: 'Top',
        placementPercent: 0,
    },
    {
        key: '2',
        name: '盈亏平衡反推广告占比',
        dailyUnits: 20,
        days: 30,
        tacosTarget: 0, // 自动计算
        cvr: 5.0,
        cpc: 1.5,
        strategy: '动态竞价',
        placement: 'Top',
        placementPercent: 20,
    },
    {
        key: '3',
        name: '设定CPC推算ACOS',
        dailyUnits: 20,
        days: 30,
        tacosTarget: 20.0,
        cvr: 8.0,
        cpc: 2.0,
        strategy: '固定竞价',
        placement: 'Product',
        placementPercent: 0,
    },
    {
        key: '4',
        name: '目标净利反推广告占比',
        dailyUnits: 20,
        days: 30,
        targetNetMargin: 10, // 目标净毛利率10%
        tacosTarget: 0, // 自动计算
        cvr: 5.0,
        cpc: 1.5,
        strategy: '动态竞价',
        placement: 'Top',
        placementPercent: 0,
    },
    {
        key: '5',
        name: '目标净利反推CPC',
        dailyUnits: 20,
        days: 30,
        targetNetMargin: 10, // 目标净毛利率10%
        tacosTarget: 30.0,
        cvr: 5.0,
        cpc: 0, // 自动计算
        strategy: '动态竞价',
        placement: 'Top',
        placementPercent: 0,
    },
    {
        key: '6',
        name: '目标净利反推转化率',
        dailyUnits: 20,
        days: 30,
        targetNetMargin: 10, // 目标净毛利率10%
        tacosTarget: 30.0,
        cvr: 0, // 自动计算
        cpc: 1.5,
        strategy: '动态竞价',
        placement: 'Top',
        placementPercent: 0,
    },
    {
        key: '7',
        name: '目标ACOS反推CPC',
        dailyUnits: 20,
        days: 30,
        targetAcos: 30, // 目标ACOS 30%
        tacosTarget: 30.0,
        cvr: 5.0,
        cpc: 0, // 自动计算
        strategy: '动态竞价',
        placement: 'Top',
        placementPercent: 0,
    }
];

/**
 * 自动计算场景数据
 * @param data 当前场景数据数组
 * @param price 目标售价
 * @param profitMarginPreAd 广告前利润率
 * @returns 更新后的数据数组，如果有变化则返回新数组，否则返回原数组
 */
export const calculateScenarioData = (
    data: ScenarioData[],
    price: number,
    profitMarginPreAd: number
): ScenarioData[] => {
    const newData = [...data];
    let hasChanges = false;

    // Row 2: 盈亏平衡 (净利=0) -> 广告占比
    // Ads% = (Price * GrossMargin% * CVR%) / (CPC * 100)
    const row2Index = newData.findIndex(item => item.key === '2');
    if (row2Index !== -1) {
        const row = newData[row2Index];
        if (row.cpc > 0 && row.cvr > 0) {
            const val = (price * profitMarginPreAd * row.cvr) / (row.cpc * 100);
            const newValue = parseFloat(val.toFixed(3));
            if (row.tacosTarget !== newValue) {
                newData[row2Index] = { ...row, tacosTarget: newValue };
                hasChanges = true;
            }
        }
    }

    // Row 4: 目标净利 -> 广告占比
    // Ads% = (Price * (GrossMargin% - TargetNet%) * CVR%) / (CPC * 100)
    const row4Index = newData.findIndex(item => item.key === '4');
    if (row4Index !== -1) {
        const row = newData[row4Index];
        if (row.cpc > 0 && row.cvr > 0 && row.targetNetMargin !== undefined) {
            const availableMargin = Math.max(0, profitMarginPreAd - row.targetNetMargin);
            const val = (price * availableMargin * row.cvr) / (row.cpc * 100);
            const newValue = parseFloat(val.toFixed(3));
            if (row.tacosTarget !== newValue) {
                newData[row4Index] = { ...row, tacosTarget: newValue };
                hasChanges = true;
            }
        }
    }

    // Row 5: 目标净利 -> CPC
    // CPC = (Price * (GrossMargin% - TargetNet%) * CVR%) / (Ads% * 100)
    const row5Index = newData.findIndex(item => item.key === '5');
    if (row5Index !== -1) {
        const row = newData[row5Index];
        if (row.tacosTarget > 0 && row.cvr > 0 && row.targetNetMargin !== undefined) {
            const availableMargin = Math.max(0, profitMarginPreAd - row.targetNetMargin);
            const val = (price * availableMargin * row.cvr) / (row.tacosTarget * 100);
            const newValue = parseFloat(val.toFixed(2));
            if (row.cpc !== newValue) {
                newData[row5Index] = { ...row, cpc: newValue };
                hasChanges = true;
            }
        }
    }

    // Row 6: 目标净利 -> 转化率
    // CVR% = (Ads% * CPC * 100) / (Price * (GrossMargin% - TargetNet%))
    const row6Index = newData.findIndex(item => item.key === '6');
    if (row6Index !== -1) {
        const row = newData[row6Index];
        if (row.tacosTarget > 0 && row.cpc > 0 && row.targetNetMargin !== undefined) {
            const availableMargin = Math.max(0, profitMarginPreAd - row.targetNetMargin);
            if (availableMargin > 0) {
                const val = (row.tacosTarget * row.cpc * 100) / (price * availableMargin);
                const newValue = parseFloat(val.toFixed(3));
                if (row.cvr !== newValue) {
                    newData[row6Index] = { ...row, cvr: newValue };
                    hasChanges = true;
                }
            }
        }
    }

    // Row 7: 目标ACOS -> CPC
    // CPC = TargetACOS% * Price * CVR% / 100
    const row7Index = newData.findIndex(item => item.key === '7');
    if (row7Index !== -1) {
        const row = newData[row7Index];
        if (row.cvr > 0 && row.targetAcos !== undefined) {
            const valCorrect = (row.targetAcos / 100) * price * (row.cvr / 100);
            const newValue = parseFloat(valCorrect.toFixed(2));
            if (row.cpc !== newValue) {
                newData[row7Index] = { ...row, cpc: newValue };
                hasChanges = true;
            }
        }
    }

    return hasChanges ? newData : data;
};
